import React, { useEffect, useState, useContext, useCallback } from "react";
import GoogleIcon from "../../../assets/images&icons/GoogleIcon.svg";
import LinkedInIcon from "../../../assets/images&icons/LinkedInIcon.svg";
import {
  LinkGoogleAccount,
  UnlinkGoogleAccount,
} from "../../../components/Auth/Providers/Google";
import AuthContext from "../../../store/AuthContext";
import {
  LinkLinkedInAccount,
  UnlinkLinkedInAccount,
} from "../../../components/Auth/Providers/LinkedIn";
import EventBus, { EventBusValues } from "../../../utils/EventBus";
import chevronRightButton from "../../../assets/Fonts/chevron-right-button.svg";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";

type Provider = {
  name: string;
  icon: string;
  handler: () => void;
  status: boolean;
  loading?: boolean;
};

export default function SingleSignOn(): React.ReactElement {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { loggedPerson } = useContext(AuthContext);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<"link" | "unlink" | null>(
    null
  );

  const handleLinkedInAction = useCallback(async () => {
    if (!loggedPerson?.firmEmail) {
      setError("User information not available");
      return;
    }

    try {
      setError(null);

      const isCurrentlyLinked = Boolean(
        loggedPerson.idProviders?.linkedinAccountId
      );

      setProviders((prev) =>
        prev.map((p) => (p.name === "LinkedIn" ? { ...p, loading: true } : p))
      );

      if (isCurrentlyLinked) {
        const handleLinkedInUnlinkSuccess = (event: any) => {
          setProviders((prev) =>
            prev.map((p) =>
              p.name === "LinkedIn"
                ? { ...p, status: false, loading: false }
                : p
            )
          );
          setError(null);

          if (loggedPerson && loggedPerson.idProviders) {
            loggedPerson.idProviders.linkedinAccountId = undefined;
          }

          window.removeEventListener(
            "linkedinUnlinkSuccess",
            handleLinkedInUnlinkSuccess
          );
          window.removeEventListener(
            "linkedinUnlinkError",
            handleLinkedInUnlinkError
          );
        };

        const handleLinkedInUnlinkError = (event: any) => {
          setError(
            event.detail?.message || "Failed to unlink LinkedIn account"
          );
          setProviders((prev) =>
            prev.map((p) =>
              p.name === "LinkedIn" ? { ...p, loading: false } : p
            )
          );
          window.removeEventListener(
            "linkedinUnlinkSuccess",
            handleLinkedInUnlinkSuccess
          );
          window.removeEventListener(
            "linkedinUnlinkError",
            handleLinkedInUnlinkError
          );
        };

        window.removeEventListener(
          "linkedinUnlinkSuccess",
          handleLinkedInUnlinkSuccess
        );
        window.removeEventListener(
          "linkedinUnlinkError",
          handleLinkedInUnlinkError
        );
        window.addEventListener(
          "linkedinUnlinkSuccess",
          handleLinkedInUnlinkSuccess
        );
        window.addEventListener(
          "linkedinUnlinkError",
          handleLinkedInUnlinkError
        );
        showConfirmationModal(
          () => UnlinkLinkedInAccount(loggedPerson.userId as string),
          "LinkedIn"
        );
        return;
      } else {
        const handleLinkedInLinkSuccess = (event: any) => {
          setProviders((prev) =>
            prev.map((p) =>
              p.name === "LinkedIn" ? { ...p, status: true, loading: false } : p
            )
          );
          setError(null);

          if (loggedPerson) {
            const linkedId = event?.detail?.linkedinAccountId || "linked";
            const idProviders =
              loggedPerson.idProviders ||
              (loggedPerson.idProviders = {} as any);
            idProviders.linkedinAccountId = linkedId;
          }

          window.removeEventListener(
            "linkedinLinkSuccess",
            handleLinkedInLinkSuccess
          );
          window.removeEventListener(
            "linkedinLinkError",
            handleLinkedInLinkError
          );
        };

        const handleLinkedInLinkError = (event: any) => {
          setError(event.detail?.message || "Failed to link LinkedIn account");
          setProviders((prev) =>
            prev.map((p) =>
              p.name === "LinkedIn" ? { ...p, loading: false } : p
            )
          );
          window.removeEventListener(
            "linkedinLinkSuccess",
            handleLinkedInLinkSuccess
          );
          window.removeEventListener(
            "linkedinLinkError",
            handleLinkedInLinkError
          );
        };

        window.removeEventListener(
          "linkedinLinkSuccess",
          handleLinkedInLinkSuccess
        );
        window.removeEventListener(
          "linkedinLinkError",
          handleLinkedInLinkError
        );
        window.addEventListener(
          "linkedinLinkSuccess",
          handleLinkedInLinkSuccess
        );
        window.addEventListener("linkedinLinkError", handleLinkedInLinkError);

        LinkLinkedInAccount();

        setTimeout(() => {
          setProviders((prev) =>
            prev.map((p) =>
              p.name === "LinkedIn" ? { ...p, loading: false } : p
            )
          );
        }, 10000);
      }
    } catch (error: any) {
      console.error("Error handling LinkedIn action:", error);
      setError(error.message || "An error occurred");
      setProviders((prev) =>
        prev.map((p) => (p.name === "LinkedIn" ? { ...p, loading: false } : p))
      );
    }
  }, [loggedPerson]);

  const handleGoogleAction = useCallback(async () => {
    if (!loggedPerson?.firmEmail) {
      setError("User information not available");
      return;
    }

    try {
      setError(null);

      const isCurrentlyLinked = Boolean(
        loggedPerson.idProviders?.googleAccountId
      );

      setProviders((prev) =>
        prev.map((p) => (p.name === "Google" ? { ...p, loading: true } : p))
      );

      if (isCurrentlyLinked) {
        const handleGoogleUnlinkSuccess = (event: any) => {
          setProviders((prev) =>
            prev.map((p) =>
              p.name === "Google" ? { ...p, status: false, loading: false } : p
            )
          );
          setError(null);

          if (loggedPerson && loggedPerson.idProviders) {
            loggedPerson.idProviders.googleAccountId = undefined;
          }

          window.removeEventListener(
            "googleUnlinkSuccess",
            handleGoogleUnlinkSuccess
          );
          window.removeEventListener(
            "googleUnlinkError",
            handleGoogleUnlinkError
          );
        };

        const handleGoogleUnlinkError = (event: any) => {
          setError(event.detail?.message || "Failed to unlink Google account");
          setProviders((prev) =>
            prev.map((p) =>
              p.name === "Google" ? { ...p, loading: false } : p
            )
          );
          window.removeEventListener(
            "googleUnlinkSuccess",
            handleGoogleUnlinkSuccess
          );
          window.removeEventListener(
            "googleUnlinkError",
            handleGoogleUnlinkError
          );
        };

        window.removeEventListener(
          "googleUnlinkSuccess",
          handleGoogleUnlinkSuccess
        );
        window.removeEventListener(
          "googleUnlinkError",
          handleGoogleUnlinkError
        );
        window.addEventListener(
          "googleUnlinkSuccess",
          handleGoogleUnlinkSuccess
        );
        window.addEventListener("googleUnlinkError", handleGoogleUnlinkError);

        showConfirmationModal(
          () => UnlinkGoogleAccount(loggedPerson.userId as string),
          "Google"
        );
        return;
      } else {
        const handleGoogleLinkSuccess = (event: any) => {
          setProviders((prev) =>
            prev.map((p) =>
              p.name === "Google" ? { ...p, status: true, loading: false } : p
            )
          );
          setError(null);

          if (loggedPerson) {
            const linkedId = event?.detail?.googleAccountId || "linked";
            const idProviders =
              loggedPerson.idProviders ||
              (loggedPerson.idProviders = {} as any);
            idProviders.googleAccountId = linkedId;
          }

          window.removeEventListener(
            "googleLinkSuccess",
            handleGoogleLinkSuccess
          );
          window.removeEventListener("googleLinkError", handleGoogleLinkError);
        };

        const handleGoogleLinkError = (event: any) => {
          setError(event.detail?.message || "Failed to link Google account");
          setProviders((prev) =>
            prev.map((p) =>
              p.name === "Google" ? { ...p, loading: false } : p
            )
          );
          window.removeEventListener(
            "googleLinkSuccess",
            handleGoogleLinkSuccess
          );
          window.removeEventListener("googleLinkError", handleGoogleLinkError);
        };

        window.removeEventListener(
          "googleLinkSuccess",
          handleGoogleLinkSuccess
        );
        window.removeEventListener("googleLinkError", handleGoogleLinkError);
        window.addEventListener("googleLinkSuccess", handleGoogleLinkSuccess);
        window.addEventListener("googleLinkError", handleGoogleLinkError);

        LinkGoogleAccount();

        setTimeout(() => {
          setProviders((prev) =>
            prev.map((p) =>
              p.name === "Google" ? { ...p, loading: false } : p
            )
          );
        }, 10000);
      }
    } catch (error: any) {
      console.error("Error handling Google action:", error);
      setError(error.message || "An error occurred");
      setProviders((prev) =>
        prev.map((p) => (p.name === "Google" ? { ...p, loading: false } : p))
      );
    }
  }, [loggedPerson]);

  useEffect(
    function setInitialStateProviders() {
      if (!loggedPerson) {
        return;
      }
      setProviders([
        {
          name: "LinkedIn",
          icon: LinkedInIcon,
          handler: handleLinkedInAction,
          status: Boolean(loggedPerson.idProviders?.linkedinAccountId),
        },
        {
          name: "Google",
          icon: GoogleIcon,
          handler: handleGoogleAction,
          status: Boolean(loggedPerson.idProviders?.googleAccountId),
        },
      ]);
    },
    [loggedPerson, handleGoogleAction, handleLinkedInAction]
  );

  useEffect(
    function handleSuccessMessage() {
      window.addEventListener("setSuccessMessage", (event: any) => {
        const { message } = event.detail;
        setSuccessMessage(message);
        if (message.includes("unlink")) {
          setMessageType("unlink");
        } else {
          setMessageType("link");
        }
      });

      if (successMessage) {
        setTimeout(() => {
          setSuccessMessage(null);
          setMessageType(null);
        }, 3000);
      }
    },
    [successMessage]
  );

  useEffect(function handleErrorMessage() {
    window.addEventListener("setErrorMessage", (event: any) => {
      const { message } = event.detail;
      setError(message);
    });
  }, []);

  function showConfirmationModal(handler: () => void, socialNetwork: string) {
    const handleDialogAction = () => {
      EventBus.dispatch(EventBusValues.CloseAllDialogs);
      setError(null);
      setSuccessMessage(null);
    };

    const resetLoadingState = () => {
      setProviders((prev) =>
        prev.map((p) =>
          p.name === socialNetwork ? { ...p, loading: false } : p
        )
      );
    };

    const handleModalClose = () => {
      resetLoadingState();
      EventBus.remove(EventBusValues.CloseAllDialogs, handleModalClose);
    };

    EventBus.on(EventBusValues.CloseAllDialogs, handleModalClose);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.removedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (
                element.querySelector &&
                element.querySelector("[data-modal-confirmation]")
              ) {
                resetLoadingState();
                observer.disconnect();
              }
            }
          });
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    EventBus.dispatch(EventBusValues.OpenDialog, {
      element: (
        <div
          className="flex flex-col items-center justify-center min-h-[120px] sm:min-h-[140px] gap-y-4 sm:gap-y-6"
          data-modal-confirmation
        >
          <span className="text-gray-font font-medium text-base sm:text-lg text-center px-2 pt-6 sm:pt-8">
            Are you sure you want to unlink your {socialNetwork} account?
          </span>

          <div className="flex flex-col sm:flex-row gap-y-2 sm:gap-y-0 gap-x-2 sm:gap-x-4 w-full sm:w-auto">
            <LegacyButton
              text="Cancel"
              color="neutral"
              className="!px-4 sm:!px-6 !py-2 sm:!py-2.5 text-sm sm:text-base w-full sm:w-auto"
              onClick={() => {
                observer.disconnect();
                EventBus.dispatch(EventBusValues.CloseAllDialogs);
              }}
            />
            <LegacyButton
              text="Confirm"
              className="!px-4 sm:!px-6 !py-2 sm:!py-2.5 text-sm sm:text-base w-full sm:w-auto"
              onClick={() => {
                observer.disconnect();
                handleDialogAction();
                handler();
              }}
            />
          </div>
        </div>
      ),
    });
  }

  return (
    <>
      <div className="w-full h-full lg:py-6 lg:pl-12 lg:pr-6 flex flex-col gap-y-4 lg:gap-y-8">
          <h4 className="font-decimal text-[#484F56] font-[600] text-left text-sm lg:text-xl">
            Single Sign-On
          </h4>
          <span className="items-start text-gray-font text-start text-sm lg:text-lg">
            Link your existing Google or LinkedIn account to log in instantly
            and securely with a single click. No new password needed!
          </span>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-center">
            {error} 
          </div>
        )}

        {successMessage && (
          <div
            className={`px-4 py-3 rounded border text-center ${
              messageType === "unlink"
                ? "bg-gray-100 border-gray-400 text-gray-700"
                : "bg-green-100 border-green-400 text-green-700"
            }`}
          >
            {successMessage} 
          </div>
        )}
        <div className="flex flex-col lg:flex-row lg:gap-12">
          {providers.map((provider, index) => (
            <div
              key={provider.name}
              className="grid grid-cols-[1fr_auto] md:grid-cols-[12rem_auto] lg:flex lg:flex-row lg:items-center lg:gap-8 items-center gap-4 md:gap-6 w-full lg:w-auto my-2 lg:my-0"
            >
                <div
                  className={`flex flex-row gap-x-2 items-center`}
                >
                <img
                  src={provider.icon}
                  alt={provider.name}
                  className="w-6 h-6 md:w-10 md:h-10"
                />
                <span className="text-gray-font font-normal text-sm md:text-base lg:text-lg">
                  {provider.name}
                </span>
              </div>
              <LegacyButton
                color={"primary"}
                text={provider.status ? "Unlink" : "Link"}
                className={`!w-[6.5rem] !md:w-[7rem] !lg:w-[9.5rem]
                  ${provider.status && "!bg-white !text-primary"} ${provider.loading && "opacity-50 cursor-not-allowed"} ${provider.status && "[&::before]:!bg-red-200"}`}
                onClick={provider.loading ? () => {} : provider.handler}
                disabled={provider.loading}
                content="textIcon"
                icon={
                  <img
                    src={chevronRightButton}
                    alt="chevron"
                    className="w-3 h-3"
                    style={{
                      filter: provider.status
                        ? "brightness(0) saturate(100%) invert(18%) sepia(72%) saturate(1846%) hue-rotate(345deg) brightness(93%) contrast(86%)"
                        : "",
                    }}
                  />
                }
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
